"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { X, ArrowRight, RotateCcw } from "lucide-react";
import useFocusTrap from "./hooks/useFocusTrap";

// Teaching source: docs/superpowers/specs/2026-04-20-admin-ux-overhaul-design.md §7.1
//
// 4-step tour triggered on first visit (!localStorage.admin_tour_done_v1).
// Steps target:
//   1. .admin-shell          — Smart Inbox overview
//   2. data-tour="command-palette"   — search / quick jump
//   3. data-tour="teaching-mode"     — teaching mode toggle
//   4. data-tour="trash"             — safety net (14-day recycle bin)
//
// If a target element is missing (e.g. CommandPalette not yet mounted in Phase 1),
// the spotlight box is skipped (null anchor) — balloon still shows.
//
// Mobile < 768px: spotlight overlay NOT rendered (too cramped); balloon only, fixed-bottom.

const STORAGE_KEY = "admin_tour_done_v1";
const VISIT_COUNT_KEY = "admin_tour_visit_count_v1";
const MAX_AUTO_SHOW = 3; // 業主反饋:看過 3 次後就不要再自動跳出

const STEPS = [
  {
    target: null,
    title: "歡迎來到咪室後台",
    body: "這裡是你的工作中心。首頁會自動整理「今天最重要的幾件事」，不用翻遍每個頁面。",
    position: "center",
  },
  {
    target: '[data-tour="command-palette"]',
    title: "快速搜尋（⌘K）",
    body: "按 ⌘K（Mac）或 Ctrl+K 可以搜尋任何頁面、訂單編號、或詢問「對帳是什麼意思」。",
    position: "bottom",
  },
  {
    target: '[data-tour="teaching-mode"]',
    title: "教學模式開關",
    body: "點這裡可以切換「新手 → 熟手 → 專注」三種模式。熟了之後切熟手，畫面更乾淨。",
    position: "bottom",
  },
  {
    target: '[data-tour="trash"]',
    title: "回收站：你的安全網",
    body: "不小心刪除的東西會在這裡保留 14 天。右下角也有「還原」按鈕，5 秒內可以立刻救回。",
    position: "top",
  },
];

/** Get bounding rect of an element, or null if not found / off-screen. */
function getTargetRect(selector) {
  if (!selector || typeof document === "undefined") return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return rect;
}

/**
 * Compute balloon position style based on target rect and preferred position.
 * Falls back to center if no anchor available.
 */
function computeBalloonStyle(rect, preferredPos) {
  if (!rect) {
    // No anchor — center horizontally, float in view
    return {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: "min(360px, calc(100vw - 40px))",
    };
  }

  const BALLOON_W = 320;
  const GAP = 12;
  const style = { position: "fixed", maxWidth: `${BALLOON_W}px` };

  if (preferredPos === "bottom") {
    style.top = rect.bottom + GAP;
    style.left = Math.max(
      20,
      Math.min(
        rect.left + rect.width / 2 - BALLOON_W / 2,
        window.innerWidth - BALLOON_W - 20,
      ),
    );
  } else {
    // top
    style.bottom = window.innerHeight - rect.top + GAP;
    style.left = Math.max(
      20,
      Math.min(
        rect.left + rect.width / 2 - BALLOON_W / 2,
        window.innerWidth - BALLOON_W - 20,
      ),
    );
  }

  return style;
}

export default function SpotlightTour() {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const balloonRef = useFocusTrap({ active, onEscape: () => dismiss() });
  const prevActiveRef = useRef(false);

  // Detect first-visit + 看過 3 次後就不再自動跳(業主仍可點右下角「重新播放」)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return; // 已主動 dismissed
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10);
    if (count >= MAX_AUTO_SHOW) return; // 看過夠多次了,不再煩
    localStorage.setItem(VISIT_COUNT_KEY, String(count + 1));
    // Small delay so the shell renders first
    const t = setTimeout(() => setActive(true), 600);
    return () => clearTimeout(t);
  }, []);

  // Detect mobile breakpoint

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Update target rect when step changes or tour activates

  useEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    const rect = getTargetRect(step?.target);
    setTargetRect(rect);
  }, [active, stepIdx]);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "1");
    setActive(false);
  }, []);

  const next = useCallback(() => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx((i) => i + 1);
    } else {
      dismiss();
    }
  }, [stepIdx, dismiss]);

  const restart = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VISIT_COUNT_KEY);
    setStepIdx(0);
    setActive(true);
  }, []);

  if (!active) {
    // Show a "re-tour" button (small, unobtrusive) for returning users
    return (
      <button
        type="button"
        onClick={restart}
        className="admin-tour-replay"
        aria-label="重新播放導覽"
        title="重新播放導覽"
      >
        <RotateCcw size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    );
  }

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const balloonStyle = isMobile
    ? {
        position: "fixed",
        bottom: "calc(76px + env(safe-area-inset-bottom))",
        left: 20,
        right: 20,
        maxWidth: "100%",
      }
    : computeBalloonStyle(targetRect, step.position);

  return (
    <>
      {/* Dimmed backdrop — full screen */}
      <div
        className="admin-tour-backdrop"
        onClick={dismiss}
        aria-hidden="true"
      />

      {/* Spotlight cutout — skip on mobile or when no target */}
      {!isMobile && targetRect && (
        <div
          className="admin-tour-spotlight"
          aria-hidden="true"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
          }}
        />
      )}

      {/* Balloon */}
      <div
        ref={balloonRef}
        role="dialog"
        aria-modal="true"
        aria-label={`導覽步驟 ${stepIdx + 1}：${step.title}`}
        className="admin-tour-balloon"
        style={balloonStyle}
      >
        {/* Header */}
        <div className="admin-tour-balloon__header">
          <span className="admin-tour-balloon__step">
            {stepIdx + 1} / {STEPS.length}
          </span>
          <button
            type="button"
            onClick={dismiss}
            className="admin-tour-balloon__close"
            aria-label="跳過導覽"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <h3 className="admin-tour-balloon__title">{step.title}</h3>
        <p className="admin-tour-balloon__body">{step.body}</p>

        {/* Footer */}
        <div className="admin-tour-balloon__footer">
          <button
            type="button"
            onClick={dismiss}
            className="admin-tour-balloon__skip"
          >
            跳過
          </button>
          <button
            type="button"
            onClick={next}
            className="admin-tour-balloon__next"
            autoFocus
          >
            {isLast ? "完成！" : "下一步"}
            {!isLast && (
              <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </>
  );
}
